import { apiFetch } from "../api/client";

/**
 * LES SOCIÉTÉS DU PARTICIPANT — celles du produit, et son rattachement à l'une d'elles.
 *
 *   GET    /domains/:d/users/:u/companies          apix — celles où il est employé
 *   GET    /v2/domains/:d/companies?siren          v1   — la recherche par identifiant
 *   POST   /domains/:d/companies/:c/users/:u       apix — le rattacher
 *   DELETE /domains/:d/companies/:c/users/:u       apix — l'en détacher
 *
 * ⚠️ TROIS TABLES, ET LA DEUXIÈME EST TOUTE L'AFFAIRE. `Companies` appartient au
 * DOMAINE, `UserEvents` à l'événement, et `Employees` est le lien entre une personne
 * et une société. Le tunnel écrit donc dans deux endroits : le rattachement
 * (`Employees`), et l'identifiant de ce rattachement sur la participation
 * (`UserEvents.employeeId`) — c'est ce dernier qui répond « pour quelle société
 * vient-il, à CET événement », puisque la même personne peut en représenter deux.
 *
 * ⚠️ LE SERVEUR POSE `employeeId` LUI-MÊME, et c'est pour ça qu'on l'appelle avec
 * `eventId`. `api/src/routes/employees/create/index.js` met à jour `UserEvents` quand
 * on le lui donne. La V1 ne le passe pas et rattrape l'affaire en écrivant
 * `employeeId` depuis le navigateur, à partir de la réponse — sauf que cette route
 * répond `{}` : mesuré dans son code (`CompaniesList.jsx`), elle écrit donc
 * `employeeId: undefined`. Un aller-retour de moins ET le champ réellement posé.
 *
 * ⚠️ LA RECHERCHE PASSE PAR L'API V1, ET C'EST DÉLIBÉRÉ. C'est la seule route qui
 * cherche une société PAR IDENTIFIANT (`Op.like`, quel que soit le pays de la fiche) ;
 * l'équivalent apix (`POST /domains/:d/companies/get`) est le filtre de grille du back
 * office, qui rend ses colonnes aliasées (`"C.name"`) et attend un langage de filtres.
 * Le client de la V2 sait viser les deux API — voir `api/client.ts` — et la règle est
 * la même que partout ailleurs ici : la route qui répond à la question, pas la route
 * de la maison.
 */

/**
 * Une société, telle que les deux routes la rendent.
 *
 * ⚠️ `siren` EST LA COLONNE, PAS LE MOT AFFICHÉ. Elle porte un SIRET, un Numero REA ou
 * un NIF selon l'événement — voir `companyIndex.ts`, et l'intitulé réglable qui va
 * avec. Renommer la propriété ici obligerait à la retraduire à chaque écriture.
 */
export type Company = {
  id: number;
  name: string;
  siren: string;
  country: string | null;
  address1: string;
  /** Le complément d'adresse, absent quand la fiche n'en porte pas. */
  address2?: string;
  postalCode: string;
  city: string;
  /**
   * Le rattachement de CE participant à cette société (`Employees.id`), quand il
   * existe. Absent d'une société simplement trouvée par son identifiant : personne
   * n'y est encore employé.
   */
  employeeId?: number;
  /**
   * THEIR JOB TITLE IN THIS COMPANY — `Employees.function`, the column the printed
   * badge shows under the name (`badgeTemplates/render` exposes it as the `function`
   * variable).
   *
   * ⚠️ IT BELONGS TO THE ATTACHMENT, NOT TO THE PERSON: the same person is a director
   * at one company and a volunteer at another. That is also why it travels with the
   * company rather than with the participant.
   *
   * ⚠️ AND IT IS ALREADY IN THE RESPONSE: the route selects `E.function`
   * (`api/src/routes/companies/findByUserId`). One more request for a word the server
   * already sends would be a round trip given away.
   */
  jobTitle?: string;
};

type RawCompany = {
  id?: number;
  name?: string | null;
  siren?: string | null;
  country?: string | null;
  address1?: string | null;
  address2?: string | null;
  postalCode?: string | null;
  city?: string | null;
  employeeId?: number | null;
  function?: string | null;
};

const text = (v: unknown) => (typeof v === "string" ? v : "");

/**
 * LE NOM DONNÉ À UNE FICHE QUI N'EN A PAS. Exporté pour qu'on puisse le reconnaître :
 * il nomme la société dans une liste, il ne doit pas partir sur une facture.
 */
export const unnamedCompany = (id: number) => `Société ${id}`;

function companyOf(raw: RawCompany): Company | null {
  if (typeof raw?.id !== "number") return null;
  return {
    id: raw.id,
    // Une fiche sans nom existe (un import, une création interrompue) : elle doit
    // rester choisissable, donc elle se nomme par son identifiant plutôt que par un
    // blanc dans la liste.
    name: text(raw.name).trim() || unnamedCompany(raw.id),
    siren: text(raw.siren),
    country: typeof raw.country === "string" ? raw.country : null,
    address1: text(raw.address1),
    ...(text(raw.address2).trim() ? { address2: text(raw.address2) } : {}),
    postalCode: text(raw.postalCode),
    city: text(raw.city),
    ...(typeof raw.employeeId === "number"
      ? { employeeId: raw.employeeId }
      : {}),
    ...(text(raw.function).trim()
      ? { jobTitle: text(raw.function).trim() }
      : {}),
  };
}

/** Ce qui s'écrit sous le nom, dans la liste : l'identifiant, puis l'adresse. */
export function resume(company: Company): string {
  const lieu = [company.postalCode, company.city].filter(Boolean).join(" ");
  return [company.siren, company.address1, lieu].filter(Boolean).join(" · ");
}

function companiesOf(answer: { companies?: RawCompany[] } | null): Company[] {
  return (answer?.companies ?? []).flatMap((c) => {
    const company = companyOf(c);
    return company ? [company] : [];
  });
}

/** Les sociétés où ce participant est employé. */
export async function fetchMyCompanies(
  domainId: number,
  userId: number,
  signal?: AbortSignal,
): Promise<Company[]> {
  const answer = await apiFetch<{ companies?: RawCompany[] }>(
    `/domains/${domainId}/users/${userId}/companies`,
    { api: "apix", signal },
  );
  return companiesOf(answer);
}

/**
 * THE COMPANIES OF ONE OF THE BEARER'S COMPANIONS — the same route, read for someone
 * the bearer registered.
 *
 * ⚠️ WITH `eventId`, AND IT IS WHAT LETS THE BEARER IN. The route is `selfOrAdmin`:
 * somebody else's companies open to an organiser, or to the bearer of a companion OF
 * THIS EVENT (`isMyCompanion`, `api/src/hooks/useHandler.js`), which takes the event
 * from the path, the query or the body. This path carries none: without the query,
 * the bearer was refused (403) on their own companion.
 */
export async function fetchCompanionCompanies(
  domainId: number,
  eventId: number,
  userId: number,
  signal?: AbortSignal,
): Promise<Company[]> {
  const answer = await apiFetch<{ companies?: RawCompany[] }>(
    `/domains/${domainId}/users/${userId}/companies?eventId=${eventId}`,
    { api: "apix", signal },
  );
  return companiesOf(answer);
}

/**
 * LES SOCIÉTÉS DU DOMAINE QUI PORTENT DÉJÀ CET IDENTIFIANT.
 *
 * ⚠️ LA ROUTE FAIT UN `LIKE`, PAS UNE ÉGALITÉ, et on ne s'en remet donc pas à elle
 * pour dire « c'est celle-ci ». Elle sert à PROPOSER : « 123456789 » remonte aussi
 * « 1234567890123 ». Le filtrage exact se fait ici, sur la même chaîne que celle qui
 * sera écrite.
 *
 * ⚠️ SANS LE PAYS. Il ne sert qu'au format de l'identifiant saisi, et il est écrit sur
 * la fiche créée (voir `Company.tsx`). La création, elle, refuse un identifiant déjà pris
 * dans le domaine QUEL QUE SOIT le pays (`SIRET_ALREADY_IN_USE`) : chercher par pays
 * laissait une impasse — SPORTAIR, pays écrit « France » au lieu de « FR », n'était pas
 * trouvée, puis sa création était refusée. La route ne filtre plus sur le pays non plus.
 */
export async function searchByIdentifier(
  domainId: number,
  identifier: string,
  signal?: AbortSignal,
): Promise<Company[]> {
  const query = new URLSearchParams({ siren: identifier });
  const answer = await apiFetch<RawCompany[]>(
    `/v2/domains/${domainId}/companies?${query}`,
    { signal },
  );
  return (Array.isArray(answer) ? answer : []).flatMap((c) => {
    const company = companyOf(c);
    return company && company.siren === identifier ? [company] : [];
  });
}

/**
 * RATTACHE le participant à cette société, POUR CET ÉVÉNEMENT.
 *
 * Une seule requête pour les deux écritures : la route insère l'`Employee` (ou le
 * désarchive, elle fusionne sur conflit) puis pose `UserEvents.employeeId` — voir la
 * note en tête de fichier.
 */
export async function attach(
  domainId: number,
  companyId: number,
  userId: number,
  eventId: number,
): Promise<void> {
  await apiFetch(`/domains/${domainId}/companies/${companyId}/users/${userId}`, {
    api: "apix",
    method: "POST",
    body: { eventId },
  });
}

/**
 * RETIENT CE RATTACHEMENT POUR L'ÉVÉNEMENT — la seconde moitié, et elle est
 * nécessaire.
 *
 * ⚠️ LA ROUTE APIX NE POSE `UserEvents.employeeId` QUE SI LA PARTICIPATION EXISTE
 * DÉJÀ : son `update` ne rencontre aucune ligne sinon, et ne dit rien. Or cette
 * étape passe AVANT la confirmation, qui est là où la V2 écrit la participation —
 * quelqu'un qui n'était pas déjà inscrit à l'événement traversait donc l'étape
 * société, voyait sa société retenue à l'écran, et arrivait à la confirmation avec
 * une participation créée sans elle. Le lien `Employees` existait ; ce qui manquait,
 * c'est POUR QUEL ÉVÉNEMENT il valait.
 *
 * Cette route-ci, elle, CRÉE la participation à défaut d'en trouver une — c'est
 * `UserEventService.createOrUpdateOne`, celle-là même que la confirmation appelle
 * ensuite pour y écrire la réponse. Un champ envoyé, un champ écrit : rien d'autre
 * n'est touché.
 */
export async function keepForEvent(
  domainId: number,
  eventId: number,
  userId: number,
  employeeId: number,
): Promise<void> {
  await apiFetch(`/domains/${domainId}/events/${eventId}/users/${userId}`, {
    method: "PUT",
    body: { employeeId },
  });
}

/**
 * Détache le participant de cette société. La fiche, elle, reste au domaine.
 *
 * ⚠️ SANS `eventId`, ET C'EST VOULU (#500). Avec lui, la route apix vide
 * `UserEvents.employeeId` de l'événement QUELLE QUE SOIT la société retirée : elle
 * relit les rattachements APRÈS les avoir supprimés, ne trouve donc jamais rien, et
 * conclut à chaque fois qu'il n'en reste aucun (`api/src/routes/employees/remove`).
 * Retirer une société secondaire faisait perdre la sienne à l'inscription. Et il n'y a
 * rien à vider : la société retenue pour l'inscription ne se retire pas, l'écran
 * l'interdit (`routes/Company.tsx`).
 */
export async function detach(
  domainId: number,
  companyId: number,
  userId: number,
): Promise<void> {
  await apiFetch(`/domains/${domainId}/companies/${companyId}/users/${userId}`, {
    api: "apix",
    method: "DELETE",
  });
}
